import * as React from "react";
import { useToggle } from "react-use";
import { BusinessReportOutcome, BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";
import { useCreateBusinessReportOutcomeNoteMutation } from "@/modules/snapshots/businessReports.api";
import { handleNoteError } from "@/modules/snapshots/components/noteErrors";

interface NoteCreationState {
  notes: BusinessReportOutcomeResult[];
  isAdding: boolean;
  toggleIsAdding: (nextValue?: boolean) => void;
  newNoteContent: string;
  setNewNoteContent: React.Dispatch<React.SetStateAction<string>>;
  isCreating: boolean;
  handleAddNote: () => Promise<void>;
  resetForm: () => void;
}

export const useNoteCreation = (
  businessReportOutcome: BusinessReportOutcome,
  columnName?: string,
): NoteCreationState => {
  const [isAdding, toggleIsAdding] = useToggle(false);
  const [newNoteContent, setNewNoteContent] = React.useState("");
  const [createNote, { isLoading: isCreating }] = useCreateBusinessReportOutcomeNoteMutation();

  const notes = React.useMemo(
    () => businessReportOutcome.result.filter(r => r.is_note && (columnName ? r.name === columnName : true)),
    [businessReportOutcome.result, columnName],
  );

  const resetForm = () => {
    setNewNoteContent("");
    toggleIsAdding(false);
  };

  const handleAddNote = async () => {
    if (isCreating) return;
    const trimmed = newNoteContent.trim();
    if (!trimmed) return;

    try {
      await createNote({
        outcomeId: businessReportOutcome.id,
        content: trimmed,
        ...(columnName ? { name: columnName } : {}),
      }).unwrap();
      resetForm();
    } catch (error) {
      handleNoteError(error, "Failed to add note");
    }
  };

  return { notes, isAdding, toggleIsAdding, newNoteContent, setNewNoteContent, isCreating, handleAddNote, resetForm };
};
